import React from 'react';
import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';
import { CardWithHeader } from './CardWithHeader';
import { ExpandModal } from './ExpandModal';
import { currencyFormatter } from '../../services/helperService';
import { graphPurpleColor } from '../../styling/stylingHelper';
import { NoDataInPeriod } from './NoDataInPeriod';

export interface ByRestaurantRow {
  restaurantId?: string;
  restaurantName: string;
  /** Revenue or spend amount (drives the right-aligned value). */
  value: number;
  /** Share of the group total, 0–100 (drives the proportional bar). */
  percentage: number;
  /** Period-over-period change %, or null/undefined to hide the change pill. */
  changePercent?: number | null;
}

interface ByRestaurantCardProps {
  title: string;
  data: ByRestaurantRow[];
  loading?: boolean;
  /** Modal open state is owned by the page so the `?expand=` deep link can open it. */
  isModalOpen: boolean;
  onMaximize: () => void;
  onCloseModal: () => void;
}

const PREVIEW_MAX_HEIGHT = 318;

const ChangePill: React.FC<{ changePercent: number }> = ({ changePercent }) => {
  const up = changePercent >= 0;
  return (
    <span
      className={
        'text-[13px] leading-[18px] tracking-[0.05px] font-semibold px-1.5 rounded-md ' +
        (up
          ? 'bg-[#069A6E]/10 text-[#069A6E] dark:bg-green-500/10 dark:text-textGreen-dark'
          : 'bg-[#D92D20]/10 text-[#D92D20] dark:bg-red-500/10 dark:text-textRed-dark')
      }
    >
      {up ? '+' : ''}{changePercent.toFixed(1)}%
    </span>
  );
};

const RestaurantRow: React.FC<{ row: ByRestaurantRow }> = ({ row }) => (
  <div className="flex flex-col gap-2.5 py-2 px-1">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[14px] leading-5 font-semibold text-textPrimary dark:text-textPrimary-dark truncate">
        {row.restaurantName}
      </span>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-[14px] leading-5 font-semibold text-textSecondary dark:text-textSecondary-dark">
          {currencyFormatter.format(row.value)}
        </span>
        {typeof row.changePercent === 'number' && <ChangePill changePercent={row.changePercent} />}
      </div>
    </div>
    <div className="w-full h-[7px] bg-[#D1D1D6] dark:bg-gray-700 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{ width: `${Math.min(row.percentage, 100)}%`, backgroundColor: graphPurpleColor }}
      />
    </div>
  </div>
);

/**
 * All-restaurants "Revenue/Spend by restaurant" card: a list of restaurants with a
 * proportional bar and an optional period-over-period change pill (no rank numbering,
 * matching the new UI). The list scrolls so 40+ restaurants don't blow up the card.
 * Maximize opens an ExpandModal with the full list; modal state is page-owned so the
 * Dashboard `?expand=` deep links can open it.
 */
export const ByRestaurantCard: React.FC<ByRestaurantCardProps> = ({
  title,
  data,
  loading = false,
  isModalOpen,
  onMaximize,
  onCloseModal,
}) => {
  const { t } = useTranslation();

  const maximizeButton = (
    <button
      type="button"
      onClick={onMaximize}
      title={t('dashboard.maximize')}
      aria-label={t('dashboard.maximize')}
      className="p-1.5 rounded-md text-textSecondary dark:text-textSecondary-dark hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <Maximize2 className="w-4 h-4" />
    </button>
  );

  const renderList = (rows: ByRestaurantRow[], scroll: boolean) => {
    if (loading) {
      return (
        <div className="w-full flex flex-col gap-3 py-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          ))}
        </div>
      );
    }
    if (rows.length === 0) {
      return <NoDataInPeriod />;
    }
    return (
      <div
        className={'w-full flex flex-col gap-3 pr-1' + (scroll ? ' overflow-y-auto custom-scroll' : '')}
        style={scroll ? { maxHeight: PREVIEW_MAX_HEIGHT } : undefined}
      >
        {rows.map((row, i) => (
          <RestaurantRow key={row.restaurantId ?? `${row.restaurantName}-${i}`} row={row} />
        ))}
      </div>
    );
  };

  return (
    <>
      <CardWithHeader title={title} headerActionsRight={maximizeButton}>
        {renderList(data, true)}
      </CardWithHeader>

      <ExpandModal isOpen={isModalOpen} onClose={onCloseModal} title={title} maxWidth="max-w-2xl">
        {renderList(data, false)}
      </ExpandModal>
    </>
  );
};

export default ByRestaurantCard;
